import React, { useEffect, useState } from "react";
import "./RespondentsOverviewSection.scss";
import ContentSection from "../components/ContentSection";
import SectionHeader from "../components/SectionHeader";
import BarChart from "../components/BarChart";
import { NormalizedSurveyData } from "../types/survey";
import { processNormalizedSurveyData } from "../utils/normalizedDataProcessing";

const RespondentsOverviewSection: React.FC = () => {
  const [data, setData] = useState<NormalizedSurveyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [chartWidth, setChartWidth] = useState(622);
  const [chartLeftMargin, setChartLeftMargin] = useState(160);

  useEffect(() => {
    import("../data/nrwc_survey_flat_final.json")
      .then((module) => {
        setData(module.default as NormalizedSurveyData[]);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load data:", err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 480) {
        setChartWidth(300);
        setChartLeftMargin(120);
      } else if (width <= 768) {
        setChartWidth(500);
        setChartLeftMargin(140);
      } else {
        setChartWidth(622);
        setChartLeftMargin(160);
      }
    };

    handleResize(); // Set initial values
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (loading) return <div>Loading data...</div>;

  // Process the data
  const { organizationTypes, servicesProvided } =
    processNormalizedSurveyData(data);

  // Convert to bar chart format
  const orgTypeBarData = organizationTypes.map((item) => ({
    type: item.type,
    avg: item.value,
  }));
  const servicesBarData = servicesProvided.map((item) => ({
    type: item.type,
    avg: item.value,
  }));

  // Calculate max values
  const maxOrgTypeCount = Math.max(...orgTypeBarData.map((d) => d.avg), 1);
  const maxServiceCount = Math.max(...servicesBarData.map((d) => d.avg), 1);

  // Color mappings
  const orgTypeColorMap: { [key: string]: string } = {
    "Local/Regional Nonprofit": "#23AF9A",
    "National Nonprofit": "#F88B1B",
    "Faith-Based Organization": "#84C141",
    "Advocacy/Policy Group": "#1C8C7A",
    "Employer/Business": "#FFD166",
    "Philanthropy/Funder": "#43C0B6",
    "Academic/Research Institution": "#B7E28F",
    "National Nonprofit Service Provider": "#FF9F68",
    "Government Agency": "#23AF9A",
    Other: "#666666",
  };

  const servicesColorMap: { [key: string]: string } = {
    "Workforce Development/Job Training": "#23AF9A",
    Housing: "#F88B1B",
    "Behavioral Health": "#84C141",
    "Legal Support": "#1C8C7A",
    "Family Reunification": "#FFD166",
    Education: "#43C0B6",
    "Peer Support": "#B7E28F",
    "Case Management": "#FF9F68",
    "Advocacy/Policy": "#23AF9A",
    Other: "#666666",
  };

  // Get "Other" responses
  const servicesOtherResponses = data
    .filter(
      (row) =>
        row.services_provided_other && row.services_provided_other.trim() !== ""
    )
    .map((row) => row.services_provided_other)
    .join(", ");

  return (
    <ContentSection className="respondents-overview-section">
      <SectionHeader
        title="Respondents Overview"
        description="Who participated in this year's survey"
      />
      <div className="respondents-overview-section-content">
        <div className="chart-wrapper">
          <h3>Organization Type</h3>
          <p className="chart-description">
            Organization types that responded by count
          </p>
          <BarChart
            data={orgTypeBarData}
            maxAvg={maxOrgTypeCount}
            colors={orgTypeBarData.map(
              (item) => orgTypeColorMap[item.type] || "#666"
            )}
            leftMargin={chartLeftMargin}
            width={chartWidth}
            height={280}
            className="responsive-chart"
          />
        </div>

        <div className="chart-wrapper">
          <h3>Service Type</h3>
          <p className="chart-description">
            Services that responding organizations provided by count
          </p>
          <BarChart
            data={servicesBarData}
            maxAvg={maxServiceCount}
            colors={servicesBarData.map(
              (item) => servicesColorMap[item.type] || "#666"
            )}
            leftMargin={chartLeftMargin + 10} // Slightly more for services
            width={chartWidth}
            height={280}
            className="responsive-chart"
          />
          {servicesOtherResponses && (
            <div className="other-responses">
              <strong>Other responses:</strong> {servicesOtherResponses}
            </div>
          )}
        </div>
      </div>
    </ContentSection>
  );
};

export default RespondentsOverviewSection;
